import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';

interface PromptFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; full_text: string, user: string; }) => Promise<void> | void;
  onEnhancePrompt?: (prompt: string) => Promise<string>;
  isEnhancing?: boolean;
  enhancementError?: string | null;
  isEnhanced?: boolean;
  hasBeenEnhanced?: boolean;
  onRevertToOriginal?: () => string;
  onToggleToEnhanced?: () => string;
  onResetEnhancementState?: () => void;
}

const PromptFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  onEnhancePrompt,
  isEnhancing = false,
  enhancementError = null,
  isEnhanced = false,
  hasBeenEnhanced = false,
  onRevertToOriginal,
  onToggleToEnhanced,
  onResetEnhancementState
}: PromptFormModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fullText, setFullText] = useState('');
  const [errors, setErrors] = useState<{ title?: string; description?: string; full_text?: string; }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { user } = useAuth();

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (!fullText.trim()) newErrors.full_text = 'Full text is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEnhancePrompt = async () => {
    if (!fullText.trim() || !onEnhancePrompt) return;

    try {
      const enhanced = await onEnhancePrompt(fullText);
      setFullText(enhanced);
    } catch (error) {
      // Parent handles error display
    }
  };

  const handleRevertToOriginal = () => {
    if (onRevertToOriginal) {
      const original = onRevertToOriginal();
      setFullText(original);
    }
  };

  const handleToggleToEnhanced = () => {
    if (onToggleToEnhanced) {
      const enhanced = onToggleToEnhanced();
      if (enhanced) {
        setFullText(enhanced);
      }
    }
  };

  const getUserId = (): string => {
    if (!user?.id) throw new Error('User not found');
    return user.id;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    if (!validate()) return;
    setLoading(true);
    try {
      await onSubmit({ title, description, full_text: fullText, user: getUserId() });
      setTitle('');
      setDescription('');
      setFullText('');
      setErrors({});
      onClose();
    } catch (err: any) {
      setApiError(err?.message || 'Failed to create prompt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="prompt-modal-overlay">
      <div className="bg-white rounded-lg p-8 w-full max-w-xl relative shadow-xl">
        {/* Close Icon - Top Right */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
          aria-label="Close"
        >
          &times;
        </button>

        {/* Modal Title */}
        <h2 className="text-2xl font-bold mb-8 text-center text-brand-dark-blue">
          {isEnhanced ? 'Enhanced Prompt' : 'Create a New Prompt'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="title" className="block text-sm font-semibold mb-1 text-left">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-left ${errors.title ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="A concise title for your prompt"
              required
              data-testid="prompt-title-input"
              disabled={loading}
            />
            {errors.title && <div className="text-red-500 text-xs mt-1 text-left">{errors.title}</div>}
          </div>

          <div className="mb-6">
            <label htmlFor="description" className="block text-sm font-semibold mb-1 text-left">Short Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-left ${errors.description ? 'border-red-400' : 'border-gray-300'}`}
              rows={2}
              placeholder="A brief summary of what the prompt does"
              required
              data-testid="prompt-description-input"
              disabled={loading}
            />
            {errors.description && <div className="text-red-500 text-xs mt-1 text-left">{errors.description}</div>}
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="full_text" className="block text-sm font-semibold text-left">Full Prompt Text</label>
              {onEnhancePrompt && onRevertToOriginal && fullText.trim() && (
                isEnhanced ? (
                  <button
                    type="button"
                    onClick={handleRevertToOriginal}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                    data-testid="toggle-to-original-button"
                  >
                    <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Show Original
                  </button>
                ) : hasBeenEnhanced && onToggleToEnhanced ? (
                  <button
                    type="button"
                    onClick={handleToggleToEnhanced}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-brand-dark-blue bg-brand-light-gray border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue transition-colors"
                    data-testid="toggle-to-enhanced-button"
                  >
                    <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Show Enhanced
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleEnhancePrompt}
                    disabled={isEnhancing || loading}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded-md hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="enhance-prompt-button"
                  >
                    {isEnhancing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Enhance
                      </>
                    )}
                  </button>
                )
              )}
            </div>
            <div className="relative">
              <textarea
                id="full_text"
                value={fullText}
                onChange={(e) => {
                  setFullText(e.target.value);
                  if ((isEnhanced || hasBeenEnhanced) && onResetEnhancementState) {
                    onResetEnhancementState();
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md text-left ${errors.full_text ? 'border-red-400' : 'border-gray-300'} ${isEnhanced ? 'border-purple-300 bg-purple-50' : ''}`}
                rows={5}
                placeholder="Paste your full AI prompt here, including all instructions and examples."
                required
                data-testid="prompt-fulltext-input"
                disabled={loading}
              />
            </div>
            {errors.full_text && <div className="text-red-500 text-xs mt-1 text-left">{errors.full_text}</div>}
            {enhancementError && (
              <div className="text-red-500 text-xs mt-1 text-left flex items-center">
                <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                </svg>
                {enhancementError}
              </div>
            )}
          </div>

          {apiError && <div className="text-red-600 text-sm mb-4 text-center">{apiError}</div>}

          <Button
            type="submit"
            className="w-full bg-brand-dark-blue hover:bg-brand-electric-blue text-white px-4 py-2 rounded text-base font-semibold"
            data-testid="prompt-submit-button"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Prompt'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PromptFormModal;
